/**
 * Full screen for the call surface.
 *
 * Why the request targets the document and not the call container
 * ---------------------------------------------------------------
 * The Fullscreen API only grants a request made while the document has transient activation -
 * in practice, synchronously inside the handler for the click that asked for it, before the
 * first `await`. Answering a call is a click, so the activation is there, but the call
 * container does not exist yet at that moment: the call surface is mounted by the state
 * change that the join produces, several awaits later (camera/microphone, then the join POST).
 * Waiting for the container means asking after the activation has been spent, which is exactly
 * the trap the popup already falls into.
 *
 * `document.documentElement` is the one element that is guaranteed to be there on the click,
 * so that is what goes full screen. The call surface is a `position: fixed; inset: 0` overlay
 * inside it, so it fills the screen either way - and when the request is refused, or the
 * engineer presses Escape, the overlay is still maximised over the app. Full screen is the
 * bonus, not the mechanism.
 *
 * Nothing here throws. A refused request is a slightly smaller call, not a failed one, and it
 * must never be reported as a call failure.
 */

import { useCallback, useEffect, useState } from 'react';

/** Whether this browser will consider a full-screen request at all. */
export function isFullscreenSupported(doc = document) {
    return !!(doc.fullscreenEnabled && doc.documentElement?.requestFullscreen);
}

/**
 * The request currently in flight, or null.
 *
 * Asking for full screen is synchronous but ENTERING it is not, and both callers ask on the
 * click and can then fail synchronously a line or two later - no socket, no secure context.
 * The failure path runs exitCallFullscreen while `fullscreenElement` is still null, so the
 * exit finds nothing to do, the request lands a tick afterwards, and the browser is left full
 * screen on an error banner with no call in it: the exact trap this module exists to prevent.
 * Exits therefore wait for a request still in flight before deciding there is nothing to do.
 */
let pendingRequest = null;

/**
 * Ask for full screen NOW.
 *
 * Call this synchronously from the click handler - never after an `await`, and never from an
 * effect, where the activation has already been spent. Returns a promise that resolves to
 * whether the request was granted, and never rejects.
 */
export function requestCallFullscreen(doc = document) {
    if (!isFullscreenSupported(doc)) return Promise.resolve(false);
    if (doc.fullscreenElement) return Promise.resolve(true);

    try {
        const result = doc.documentElement.requestFullscreen({ navigationUI: 'hide' });
        if (!result || typeof result.then !== 'function') return Promise.resolve(true);
        const settled = result.then(() => true, () => false);
        pendingRequest = settled;
        settled.then(() => {
            // Only clear our own: a later request may already have replaced it.
            if (pendingRequest === settled) pendingRequest = null;
        });
        return settled;
    } catch {
        // Older engines throw synchronously rather than rejecting
        return Promise.resolve(false);
    }
}

/** Leave full screen, if we are in it. Safe to call when we are not. */
export function exitCallFullscreen(doc = document) {
    // Never decide there is nothing to leave while a request is still on its way in.
    return (pendingRequest || Promise.resolve()).then(() => {
        if (!doc.fullscreenElement || !doc.exitFullscreen) return undefined;
        try {
            return Promise.resolve(doc.exitFullscreen()).catch(() => {});
        } catch {
            // Older engines throw synchronously rather than rejecting
            return undefined;
        }
    });
}

/**
 * Live full-screen state, plus a toggle for the in-call button.
 *
 * The state is read from the document rather than remembered, because the engineer can leave
 * full screen without touching our button at all (Escape, F11, the window manager) and a
 * button that then still says "Exit full screen" is worse than no button.
 */
export function useCallFullscreen() {
    const [isFullscreen, setIsFullscreen] = useState(() => !!document.fullscreenElement);

    useEffect(() => {
        const sync = () => setIsFullscreen(!!document.fullscreenElement);
        sync();
        document.addEventListener('fullscreenchange', sync);
        return () => document.removeEventListener('fullscreenchange', sync);
    }, []);

    const toggle = useCallback(() => {
        if (document.fullscreenElement) {
            exitCallFullscreen();
        } else {
            // A button press is its own activation, so this one can ask directly.
            requestCallFullscreen();
        }
    }, []);

    return {
        isFullscreen,
        isSupported: isFullscreenSupported(),
        enter: requestCallFullscreen,
        exit: exitCallFullscreen,
        toggle
    };
}

export default useCallFullscreen;
